// V2.3 — Unified auth context (customer + admin share the same JWT storage).
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getToken, setToken, clearToken } from "@/lib/auth";
import { Sentry } from "@/lib/sentry";

const AuthCtx = createContext(null);

// V2.15.9 — Tag Sentry with the authenticated user id only (no email / PII).
function tagSentryUser(u) {
  try {
    if (u && u.id) {
      Sentry.setUser({ id: u.id });
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* Sentry not initialized → no-op */
  }
}

export function AuthProvider({ children }) {
  // undefined = still probing /auth/me ; null = not authenticated ; object = user
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      tagSentryUser(data);
      return data;
    } catch {
      clearToken();
      setUser(null);
      tagSentryUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // V2.10 — MFA gate: return the pending payload so the caller can route
    // to /mfa-verify. Do NOT persist the pending token (it is not usable for
    // regular API calls).
    if (data.mfa_required) {
      return { mfa_required: true, temp_token: data.access_token, user: data.user };
    }
    setToken(data.access_token);
    setUser(data.user);
    tagSentryUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    return data;
  };

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    tagSentryUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, signup, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}

/** Normalize FastAPI 422/validation errors into a single readable string. */
export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
